import * as UiCard from '@/components/ui/card'
import { Text } from '@/components/ui/text'
import { AppTooltip } from '@components/AppTooltip'
import SetupAuthor from '@components/SetupAuthor'
import SetupTitle from '@components/SetupTitle'
import type { Tables } from '@hvf/contracts'
import { useNavigate } from '@solidjs/router'
import { type Component, Match, Show, Switch } from 'solid-js'
import { useService } from 'solid-services'
import { css } from 'styled-system/css'
import { Box, Flex, Grid } from 'styled-system/jsx'
import { useCreate } from '../../Create/state'
import Visualisation from '../../Visualisation'; // move to appropriate spot
import type { EnrichedSetup } from '../state'
import { useFilters } from '../state/filter'
import { promptTooltipClass } from '../../promptTooltipClass'
import Details from './Details'
import { Images } from './Images'
import { SocialBadges } from '../SocialBadges'
import { UpdateButton } from '../UpdateButton'

interface ItemProps {
  setup?: EnrichedSetup
  layout: 'noramlised' | 'image'
  hideEditButton?: boolean
}

const Card: Component<ItemProps> = (props) => {
  const navigate = useNavigate()
  const { fetchFilters } = useService(useFilters)()
  const { editSetup } = useService(useCreate)()

  const isLong = props.setup?.position === 'long'

  //   "The Pattern Preview is changed to grey under the following statuses:
  // Spoiled, failed, stopped out, full target, overperformance closed.
  // In the case where a Full Target Setup is changed to Overperformance the pattern preview is displayed in colour.
  // "
  const greyedOutFor: Tables<'setup'>['stage'][] = ['spoiled', 'stopped-out', 'target', 'overperformance-closed', 'timed-out']
  const isgreyed = () => greyedOutFor.some(search => search === props.setup?.stage)

  return (
    <Show when={props.setup}>
      <UiCard.Root
        cursor="pointer"
        onClick={(e) => navigate(`/setups/${props.setup?.id}`)}
        class={css({
          filter: isgreyed() ? 'grayscale(1)' : 'none',
          display: 'flex',
          width: '100%',
          position: 'relative',
          flexDirection: 'column',
          // Keep your base transition for the transform and box-shadow
          transition: 'all 0.2s',
          bg: 'linear-gradient(0deg,var(--colors-gray-surface-bg) 0%, var(--colors-gray-surface-bg) 30%)',
          isolation: 'isolate',

          _hover: {
            boxShadow: 'var(--shadows-editorial-lift)',
            transform: 'translateY(-6px)',
          },

          '&:hover [data-slot="dintro"]::before': {
            opacity: 1,
          }
        })}
      >
        <UiCard.Header
          display='grid'
          gridTemplateColumns='1fr auto'
          alignItems='center'
          px='15px'
          pt='14px'
          pb='2'
        >
          <AppTooltip
            delay={900}
            content='Ticker & direction'
            contentClass={promptTooltipClass}
          >
            <SetupTitle
              setup={props.setup}
              fontSize='15.5px'
              iconSize={10}
              gap='7px'
            />
          </AppTooltip>

          <SocialBadges
            setup={props.setup}
            gap='1.5'
          />

        </UiCard.Header>

        <UiCard.Body
          gap='2'
          px='15px'
          pt='0'
          pb='0'
        >
          <Grid gap={1}>
            <Switch>
              <Match when={props.layout === 'noramlised'}>
                <Visualisation
                  height="300px"
                  setup={props.setup!}
                />
              </Match>
              <Match when={props.layout === 'image'} >
                <Box
                  onClick={(e) => {
                    e.stopPropagation()
                  }}>
                  <Images setup={props.setup} />
                </Box>
              </Match>
            </Switch>
            {/* <DetailsOverlaid setup={props.setup} /> */}
          </Grid>
          <Box
            data-slot="dintro"
            position="relative"
            class={css({
              _before: {
                content: '""',
                position: 'absolute',
                left: '-15px',
                right: '-15px',
                top: 0,
                bottom: 0,
                bg: 'linear-gradient(to bottom, rgba(20, 22, 27, 0.13), transparent)',
                opacity: 0,
                transition: 'opacity 0.35s',
                zIndex: 0,
                pointerEvents: 'none',
              },
            })}
          >
            <Grid
              gap='1'
              position="relative"
              zIndex={1}
            >
              <AppTooltip content={props.setup?.title} delay={1300}>
                <Text
                  fontSize='17px'
                  fontWeight='600'
                  lineHeight='1.26'
                  letterSpacing='-.02em'
                  lineClamp={2}
                  minHeight='2.52em'
                  color="editorialInk"
                >
                  {props.setup?.title}
                </Text>
              </AppTooltip>
              <Text
                fontSize='12.5px'
                lineHeight='1.45'
                minHeight='2.9em'
                lineClamp={2}
                color="editorialInk3"
              >
                {props.setup?.description}
              </Text>
            </Grid>
          </Box>
          <Details setup={props.setup} />
        </UiCard.Body>

        <UiCard.Footer
          gap={2}
          px='15px'
          pt='2'
          pb='13px'
          display="grid"
          justifyContent="stretch"
        >
          <Flex justifyContent="space-between" alignItems='end'>
            <SetupAuthor
              createdBy={props.setup?.created_by}
              created_at={props.setup?.created_at}
            />

            <Flex
              gap={2}
              alignItems="center"
            >
              <Show when={!props.hideEditButton}>
                <UpdateButton
                  size='card'
                  onClick={() => editSetup(props.setup as any)}
                />
              </Show>
            </Flex>
          </Flex>
        </UiCard.Footer>
      </UiCard.Root>
    </Show>
  )
}

export default Card
